import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Dialog } from '@headlessui/react';
import { ShieldCheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { verificationApi } from '../../services/api';
import { useVerificationStore } from '../../stores/verificationStore';
import { Alert } from '../shared/Alert';
import { LoadingSpinner } from '../shared/LoadingSpinner';

const consentSchema = z.object({
  acceptDataCollection: z.literal(true, {
    errorMap: () => ({ message: 'You must accept data collection' }),
  }),
  acceptHomeAffairsQuery: z.literal(true, {
    errorMap: () => ({ message: 'You must accept the Home Affairs query' }),
  }),
  acceptRetentionPolicy: z.literal(true, {
    errorMap: () => ({ message: 'You must accept the data retention policy' }),
  }),
  acceptPopiaRights: z.literal(true, {
    errorMap: () => ({ message: 'You must acknowledge your POPIA rights' }),
  }),
});

type ConsentFormData = z.infer<typeof consentSchema>;

interface ConsentStepProps {
  onNext: () => void;
  onCancel: () => void;
}

export function ConsentStep({ onNext, onCancel }: ConsentStepProps) {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { requestId, setConsent, setStep } = useVerificationStore();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ConsentFormData>({
    resolver: zodResolver(consentSchema),
    defaultValues: {
      acceptDataCollection: false as unknown as true,
      acceptHomeAffairsQuery: false as unknown as true,
      acceptRetentionPolicy: false as unknown as true,
      acceptPopiaRights: false as unknown as true,
    },
  });

  const onSubmit = async (data: ConsentFormData) => {
    if (!requestId) return;

    setError(null);
    setIsLoading(true);

    try {
      await verificationApi.recordConsent(requestId, data);
      setConsent(data);
      setStep('personal-info');
      onNext();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Failed to record consent');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={true} onClose={onCancel} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-2xl w-full bg-white rounded-xl shadow-xl max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <ShieldCheckIcon className="h-8 w-8 text-primary-600" />
                <Dialog.Title className="text-xl font-bold text-gray-900">
                  Consent & Privacy Notice
                </Dialog.Title>
              </div>
              <button
                type="button"
                onClick={onCancel}
                className="text-gray-400 hover:text-gray-500"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit as Parameters<typeof handleSubmit>[0])} className="p-6 space-y-6">
            {error && <Alert type="error" message={error} />}

            <div className="prose prose-sm max-w-none text-gray-600">
              <p>
                Before proceeding with your marital status verification, please read and accept the
                following terms in compliance with the Protection of Personal Information Act (POPIA).
              </p>
            </div>

            <div className="space-y-4">
              {/* Data Collection Consent */}
              <div className="bg-gray-50 rounded-lg p-4">
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    {...register('acceptDataCollection')}
                  />
                  <div>
                    <span className="font-medium text-gray-900">Data Collection</span>
                    <p className="text-sm text-gray-600 mt-1">
                      I consent to the collection of my personal information including my name, ID
                      number, date of birth, address, phone number, selfie photograph, and identity
                      document for the purpose of verifying my marital status.
                    </p>
                  </div>
                </label>
                {errors.acceptDataCollection && (
                  <p className="error-text ml-7">{errors.acceptDataCollection.message}</p>
                )}
              </div>

              {/* Home Affairs Query Consent */}
              <div className="bg-gray-50 rounded-lg p-4">
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    {...register('acceptHomeAffairsQuery')}
                  />
                  <div>
                    <span className="font-medium text-gray-900">Department of Home Affairs Query</span>
                    <p className="text-sm text-gray-600 mt-1">
                      I authorize Am I Secretly Married to query the Department of Home Affairs database on my
                      behalf to retrieve my marital status information using my South African ID
                      number.
                    </p>
                  </div>
                </label>
                {errors.acceptHomeAffairsQuery && (
                  <p className="error-text ml-7">{errors.acceptHomeAffairsQuery.message}</p>
                )}
              </div>

              {/* Data Retention Policy */}
              <div className="bg-gray-50 rounded-lg p-4">
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    {...register('acceptRetentionPolicy')}
                  />
                  <div>
                    <span className="font-medium text-gray-900">Data Retention Policy</span>
                    <p className="text-sm text-gray-600 mt-1">
                      I understand that my personal information will be retained for{' '}
                      <strong>30 days</strong> after the completion of my verification request, after
                      which it will be securely deleted. Audit logs may be retained for compliance
                      purposes.
                    </p>
                  </div>
                </label>
                {errors.acceptRetentionPolicy && (
                  <p className="error-text ml-7">{errors.acceptRetentionPolicy.message}</p>
                )}
              </div>

              {/* POPIA Rights */}
              <div className="bg-gray-50 rounded-lg p-4">
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    {...register('acceptPopiaRights')}
                  />
                  <div>
                    <span className="font-medium text-gray-900">Your Rights Under POPIA</span>
                    <p className="text-sm text-gray-600 mt-1">
                      I acknowledge that under POPIA, I have the right to:
                    </p>
                    <ul className="text-sm text-gray-600 mt-2 list-disc list-inside space-y-1">
                      <li>Request access to my personal information</li>
                      <li>Request correction of inaccurate information</li>
                      <li>Request deletion of my personal information</li>
                      <li>Object to the processing of my personal information</li>
                      <li>Lodge a complaint with the Information Regulator</li>
                    </ul>
                  </div>
                </label>
                {errors.acceptPopiaRights && (
                  <p className="error-text ml-7">{errors.acceptPopiaRights.message}</p>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
              <button type="button" onClick={onCancel} className="btn-secondary">
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={isLoading}>
                {isLoading ? <LoadingSpinner size="sm" /> : 'I Accept & Continue'}
              </button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
